import { Box } from "@/components/layout/flexbox";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";

/**
 * Props for NotificationCheckbox component
 */
interface NotificationCheckboxProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

/**
 * A checkbox component with label and description for notification settings
 */
function NotificationCheckbox({
  label,
  description,
  checked,
  onChange,
}: NotificationCheckboxProps) {
  const handleClick = () => {
    onChange();
  };

  return (
    <div className="flex flex-col md:gap-1 xs:gap-0.5">
      <div className="flex items-center">
        <StandaloneCheckbox onChange={onChange} checked={checked} />
        <Clickable onClick={handleClick}>
          <Box style={{ fontSize: 16 }} ml={2}>
            {label}
          </Box>
        </Clickable>
      </div>
      <div className="text-sm text-subtext">{description}</div>
    </div>
  );
}

/**
 * Props for NotificationSettings component
 */
interface NotificationSettingsProps {
  requiredSendLaunchEmail: boolean;
  onRequiredSendLaunchEmailChange: (value: boolean) => void;
  requiredSendDueEmail: boolean;
  onRequiredSendDueEmailChange: (value: boolean) => void;
  requiredSendPastDueEmail: boolean;
  onRequiredSendPastDueEmailChange: (value: boolean) => void;
  showDueAndPastDueNotifications: boolean;
  launchNotificationLabel?: string;
}

/**
 * Component for configuring notification settings for a program element
 */
export function NotificationSettings({
  requiredSendLaunchEmail,
  onRequiredSendLaunchEmailChange,
  requiredSendDueEmail,
  onRequiredSendDueEmailChange,
  requiredSendPastDueEmail,
  onRequiredSendPastDueEmailChange,
  showDueAndPastDueNotifications,
  launchNotificationLabel = "Initial notification",
}: NotificationSettingsProps) {
  const handleLaunchEmailChange = () => {
    onRequiredSendLaunchEmailChange(!requiredSendLaunchEmail);
  };

  const handleDueEmailChange = () => {
    onRequiredSendDueEmailChange(!requiredSendDueEmail);
  };

  const handlePastDueEmailChange = () => {
    onRequiredSendPastDueEmailChange(!requiredSendPastDueEmail);
  };

  return (
    <StyledFormGroup label="Notification">
      <NotificationCheckbox
        label={launchNotificationLabel}
        description="Sends a notification when the component becomes available to the participant."
        checked={requiredSendLaunchEmail}
        onChange={handleLaunchEmailChange}
      />
      {showDueAndPastDueNotifications && (
        <>
          <NotificationCheckbox
            label="Due soon notification"
            description="Sends a reminder notification when the due date is approaching."
            checked={requiredSendDueEmail}
            onChange={handleDueEmailChange}
          />

          <NotificationCheckbox
            label="Past due notification"
            description="Sends a notification if the component is not completed by the due date."
            checked={requiredSendPastDueEmail}
            onChange={handlePastDueEmailChange}
          />
        </>
      )}
    </StyledFormGroup>
  );
}
